import React, { useEffect, useRef, useState, useCallback } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import { PayloadAction } from '@reduxjs/toolkit';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Spin, Row, Col, Select, Button } from 'antd';
import queryString from 'query-string';
import pluralize from 'pluralize';
import { useDidUpdate } from 'rooks';

import {
  CustomerStatusFilterEnum,
  CustomerInfoResponseModel,
  CustomerPagedResponseModel,
} from 'api/backofficeApiClient';
import ClientCard from '../ClientCard';
import { getClientStatusName } from 'utils/clientUtils';
import { getClientRequests } from 'redux/reducers/clientSlice';
import { clientCreatedAtSelector } from 'redux/selectors/clientSelectors';
import BaseSearch from 'components/wrappers/BaseSearch';
import { ClientQueryType } from '../ApprovedClientsSection';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';

import styles from './ClientRequestsSection.module.less';

type Props = {
  isAdmin?: boolean;
};

const statuses = [
  {
    label: getClientStatusName(CustomerStatusFilterEnum.All),
    value: CustomerStatusFilterEnum.All,
  },
  {
    label: getClientStatusName(CustomerStatusFilterEnum.Requested),
    value: CustomerStatusFilterEnum.Requested,
  },
];
export interface ClientsFilters {
  statusFilter: CustomerStatusFilterEnum;
}
export default function ClientRequestsSection(props: Props) {
  const { isAdmin } = props;
  const latestRequestId = useRef(0);
  const infiniteScrollParentRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { query, statusFilter, section } = queryString.parse(location.search);
  const initialFilters: ClientsFilters = {
    statusFilter: +statusFilter || CustomerStatusFilterEnum.All,
  };
  const [filters, setFilters] = useState<ClientsFilters>(initialFilters);
  const [clients, setClients] = useState<CustomerInfoResponseModel[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
  const clientCreatedAt = useAppSelector(clientCreatedAtSelector);
  const dispatch = useAppDispatch();

  useDidUpdate(() => {
    const queryData: ClientQueryType = {
      query,
      section,
      statusFilter: filters.statusFilter,
    };
    navigate(`${location.pathname}?${queryString.stringify(queryData)}`, {
      replace: true,
    });
  }, [filters, query]);

  useEffect(() => {
    initInfinityScroll();
  }, [filters, clientCreatedAt, query]);

  const initInfinityScroll = () => {
    setIsVisible(false);
    setClients([]);
    setIsInitialDataLoaded(false);
    setHasMore(true);
    setTotalCount(0);
    const timer = setTimeout(() => {
      // to destroy InfiniteScroll
      setIsVisible(true);
    }, 0);

    return () => clearTimeout(timer);
  };

  const handleStatusChange = (value: CustomerStatusFilterEnum) => {
    setFilters({ ...filters, statusFilter: value });
  };

  const getTotalCountLabel = () => {
    const requestsWord = pluralize('request', totalCount);

    return `${totalCount} submitted customer creation ${requestsWord}`;
  };

  const getNotFoundMessage = () => {
    switch (filters.statusFilter) {
      case CustomerStatusFilterEnum.Requested:
      case CustomerStatusFilterEnum.All:
      default:
        return 'Great job! None of your customers are pending.';
    }
  };

  const onSetClients = useCallback(
    (result: PayloadAction<CustomerPagedResponseModel>) => {
      const loadedClients = result.payload.details || [];
      const totalCount = result.payload.totalCount || 0;
      const newClients = [...clients, ...loadedClients];
      const hasMoreRows = newClients.length < totalCount;

      setHasMore(hasMoreRows);
      setClients(newClients);
      setTotalCount(totalCount);
    },
    [clients],
  );

  const onLoadMore = useCallback(
    async (pageNumber: number) => {
      const requestId = Date.now();
      latestRequestId.current = requestId;

      const result = await dispatch(
        getClientRequests({
          statusFilter: filters.statusFilter,
          pageNumber,
          requestId,
          searchQuery: query ? query.trim() : undefined,
          isAdminRole: isAdmin,
        }),
      );

      if (result.meta.arg.requestId === latestRequestId.current) {
        if (getClientRequests.fulfilled.match(result)) {
          onSetClients(result);
        } else {
          setHasMore(false);
        }

        setIsInitialDataLoaded(true);
      }
    },
    [dispatch, onSetClients, filters, isAdmin, query],
  );

  return (
    <div ref={infiniteScrollParentRef} className="page-wrapper scroll-box">
      <div className="page-box page-holder page-inner">
        <div className="page-inner-container">
          <Row gutter={[20, 10]}>
            <Col xs={24} md={6}>
              <BaseSearch
                searchQuery={query}
                placeholder="Search by customer name, ID"
              />
            </Col>
            <Col xs={24} md={6}>
              <Select
                className={styles.submittedSelect}
                value={filters.statusFilter}
                placeholder="Select status"
                onChange={handleStatusChange}
              >
                {statuses.map((status) => (
                  <Select.Option
                    key={`select-status-${status.value}`}
                    value={status.value}
                  >
                    {status.label}
                  </Select.Option>
                ))}
              </Select>
            </Col>

            {isAdmin && (
              <Col flex="auto">
                <Button className={styles.btn}>
                  <Link to="/customer-bulk-import">Import customers</Link>
                </Button>
              </Col>
            )}

            <Col xs={24}>
              {isInitialDataLoaded && (
                <>
                  {!!totalCount && (
                    <p className="page-total-count">{getTotalCountLabel()}</p>
                  )}

                  {!totalCount && (
                    <span className="no-data-msg">{getNotFoundMessage()}</span>
                  )}
                </>
              )}
            </Col>
          </Row>
          {isVisible && (
            <InfiniteScroll
              pageStart={-1}
              loadMore={onLoadMore}
              hasMore={hasMore}
              loader={<Spin key={0} />}
              useWindow={false}
              getScrollParent={() => infiniteScrollParentRef.current}
            >
              <Row gutter={[20, 20]}>
                {clients.map((client, index) => (
                  <Col
                    key={`${client.customerId}-${index}`}
                    xs={24}
                    md={12}
                    className="d-flex"
                  >
                    <ClientCard client={client} className="stretch-card" />
                  </Col>
                ))}
              </Row>
            </InfiniteScroll>
          )}
        </div>
      </div>
    </div>
  );
}
